import { ReactNode, useEffect, useState } from "react";
import { Flex, Typography, Spin, Alert } from "antd";
import { observer } from "mobx-react-lite";

import { BALANCE_ENUM, BalanceEventData, BID_STATUSES, BidRequestStatus } from "interfaces";
import { WhiteBoxBlock } from "ui";
import { CardSteps } from "components";
import { dateFormat, getShortName, useBalanceEvents } from "utils";
import { useStores } from "stores";

import { getDescription, getStatusTitle } from "./utils";

import styles from "./bidSteps.module.css";

export const BidSteps = observer(() => {
  const { bidCard, user, bidHistory } = useStores();

  const { subscribeToBalance, isConnected } = useBalanceEvents();
  const [tokenAmountBalance, setTokenAmountBalance] = useState<BalanceEventData | null>(null);
  const [nativeAmountBalance, setNativeAmountBalance] = useState<BalanceEventData | null>(null);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribeToken = subscribeToBalance(BALANCE_ENUM.TOKEN, (event) => {
      if (event.requestNumber === bidCard.bid.id) {
        setTokenAmountBalance(event);
      }
    });
    const unsubscribeNative = subscribeToBalance(BALANCE_ENUM.NATIVE, (event) => {
      if (event.requestNumber === bidCard.bid.id) {
        setNativeAmountBalance(event);
      }
    });

    return () => {
      unsubscribeToken();
      unsubscribeNative();
    };
  }, [isConnected, subscribeToBalance, bidCard.bid.id]);

  const tokenAmount = tokenAmountBalance?.amount ?? bidCard.bid.balance?.token;
  const nativeAmount = nativeAmountBalance?.amount ?? bidCard.bid.balance?.native;

  const { isRiskManager, isSeniorOperator, isJuniorOperator } = user.roles;

  const showInitiator = isRiskManager || isSeniorOperator || isJuniorOperator;

  const items = bidHistory.stepStatuses.map((statusInfo, index) => {
    const hasHistoryStatus = bidHistory.statusHistory.some((item) => item.status === statusInfo.status);

    return {
      rawStatus: statusInfo.status as BidRequestStatus,
      title: getStatusTitle(statusInfo.status),
      date: statusInfo.statusDate ? dateFormat(statusInfo.statusDate, true) : undefined,

      description: hasHistoryStatus
        ? getDescription({
            bid: bidCard.bid,
            userRoles: user.roles,
            processingHistoryTooltip: bidHistory.processingHistoryTooltip,
            stepStatuses: statusInfo.status as BidRequestStatus,
          })
        : undefined,
      initiator: showInitiator
        ? {
            id: statusInfo?.userId ?? "",
            fullName: getShortName(statusInfo?.surname ?? "", statusInfo?.name ?? "", statusInfo.secondName),
          }
        : undefined,
    };
  });

  return (
    <>
      <Spin spinning={bidHistory.loading || bidCard.isPending}>
        {bidHistory.fetchError && (
          <WhiteBoxBlock>
            <Flex vertical gap="small">
              <Typography.Text>Не удалось загрузить историю изменения статусов</Typography.Text>
              <Alert showIcon type="error" message={bidHistory.fetchError.title} />
            </Flex>
          </WhiteBoxBlock>
        )}
        {items.length > 0 && (
          <CardSteps
            items={items}
            currentStatus={bidCard.bid.status}
            currentIndex={items.findLastIndex((item) => item.rawStatus === bidCard.bid.status)}
          />
        )}
      </Spin>
    </>
  );
});

export const BidStepsContainer: React.FC<{
  children: ReactNode;
}> = observer(({ children }) => {
  return (
    <Flex gap="large">
      <Flex className={styles.statusHistoryBlock} id="bid-steps">
        <BidSteps />
      </Flex>
      <Flex vertical gap="large" className="flex-grow overflow-auto">
        {children}
      </Flex>
    </Flex>
  );
});
